import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { CashbackFacade } from '@features/cashback/facades/cashback.facade';
import { CashbackFormData } from '@features/cashback/types';
import { PointsSummaryFacade } from '@features/points-summary/facades/points-summary-facade/points-summary.facade';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { getAuthIsLoggedIn } from '@rd/core/auth/store/selectors/auth.selectors';
import { getLoyaltyCurrencySelected } from '@rd/core/loyalty-currency/store/selectors/loyalty-currency.selectors';
import { AnalyticsService } from '@rd/core/services/analytics/analytics.service';
import { FeatureFlagService } from '@rd/core/services/feature-flag/feature-flag.service';
import { PageTitleService } from '@rd/core/services/page-title/page-title.service';
import { CoreState } from '@rd/core/store/reducers';
import { DialogService } from '@rd/popup/services/dialog/dialog.service';
import { PopupSettingService } from '@rd/popup/services/popup-setting/popup-setting.service';
import { CheckoutErrorDialogComponent } from '@rd/shared-legacy/components/checkout-error-dialog/checkout-error-dialog.component';
import { CashBackMembership, LoyaltyCurrency, LoyaltyProgram } from '@rd/types';
import { combineLatest } from 'rxjs';
import { first, map, take } from 'rxjs/operators';
import { RedeemCashSettingService } from '../../services/redeem-cash-setting/redeem-cash-setting.service';
import { LoadMemberships } from '../../store/actions/membership.actions';
import { LoadRedeemCashLoyaltyProgram } from '../../store/actions/redeem-cash-loyalty-program.actions';
import { RedeemCashV2State } from '../../store/reducers';
import { getMembershipsOrUpdateMembershipIsLoading } from '../../store/selectors/membership.selectors';
import {
  getPreferredRedeemCashLoyaltyProgram,
  getRedeemCashLoyaltyProgramOrMembershipsAreLoading,
  getRedeemCashMembership,
} from '../../store/selectors/redeem-cash-combined.selectors';

@UntilDestroy()
@Component({
  selector: 'rd-rcv2-checkout',
  templateUrl: './checkout.component.html',
  styleUrls: ['./checkout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RcV2CheckoutComponent implements OnInit {
  @HostBinding('class.rd-rcv2-checkout') componentClass = true;

  redeemCashMembership$ = this.store.select(getRedeemCashMembership);

  redeemCashLoyaltyProgram$ = this.store.select(
    getPreferredRedeemCashLoyaltyProgram,
  );

  redeemCashLoyaltyProgramOrMembershipsAreLoading$ = this.store.select(
    getRedeemCashLoyaltyProgramOrMembershipsAreLoading,
  );

  membershipsOrUpdateMembershipIsLoading$ = this.store.select(
    getMembershipsOrUpdateMembershipIsLoading,
  );

  checkoutFormData$ = this.cashbackFacade.cashbackForm$;

  loyaltyCurrencySelected$ = this.store.select(getLoyaltyCurrencySelected);

  isLoggedIn$ = this.store.select(getAuthIsLoggedIn);

  pointsSummary$ = this.pointsSummaryFacade.pointsSummary$;

  vm$ = combineLatest([
    this.redeemCashLoyaltyProgram$,
    this.redeemCashLoyaltyProgramOrMembershipsAreLoading$,
    this.redeemCashMembership$,
    this.membershipsOrUpdateMembershipIsLoading$,
    this.checkoutFormData$,
    this.loyaltyCurrencySelected$,
    this.isLoggedIn$,
    this.pointsSummary$,
  ]).pipe(
    map(
      ([
        redeemCashLoyaltyProgram,
        redeemCashLoyaltyProgramOrMembershipsAreLoading,
        redeemCashMembership,
        membershipsOrUpdateMembershipIsLoading,
        checkoutFormData,
        loyaltyCurrencySelected,
        isLoggedIn,
        pointsSummary,
      ]) => ({
        redeemCashLoyaltyProgram,
        redeemCashLoyaltyProgramOrMembershipsAreLoading,
        redeemCashMembership,
        membershipsOrUpdateMembershipIsLoading,
        checkoutFormData,
        loyaltyCurrencySelected,
        isLoggedIn,
        pointsSummary,
        // in case the current redeemCashMembership has enrollment
        // with status not `active` then we force user to relink
        isFormEnable:
          !!redeemCashMembership &&
          this.validateMembershipEnrollment(redeemCashMembership),
      }),
    ),
  );

  constructor(
    private store: Store<{ core: CoreState; redeemCashV2: RedeemCashV2State }>,
    private router: Router,
    private dialogService: DialogService,
    private translateService: TranslateService,
    private analyticsService: AnalyticsService,
    private popupSettingService: PopupSettingService,
    private pointsSummaryFacade: PointsSummaryFacade,
    private pageTitleService: PageTitleService,
    private redeemCashSettingService: RedeemCashSettingService,
    private featureFlagService: FeatureFlagService,
    private cashbackFacade: CashbackFacade,
  ) {}

  ngOnInit(): void {
    // Initialise variables from store
    this.loadMembershipsIfLoggedIn();

    this.loadRedeemCashLoyaltyProgramIfItDoesntExist();

    // Initialise variables from setting flag
    // Analytics and page title

    this.pageTitleService.setPageTitle('redeem-cash-checkout');
    this.analyticsService.track({
      // @ts-ignore
      routeName: this.analyticsService.sitemap.redeemCash.checkout,
    });

    this.redeemCashLoyaltyProgram$
      .pipe(
        first((redeemCashLoyaltyProgram) => !!redeemCashLoyaltyProgram),
        untilDestroyed(this),
      )
      // @ts-ignore
      .subscribe((redeemCashLoyaltyProgram: LoyaltyProgram) => {
        if (redeemCashLoyaltyProgram) {
          this.analyticsService.track({
            routeName:
              // @ts-ignore
              this.analyticsService.sitemap.redeemCash.checkoutFullData,
          });
        }
      });
  }

  submitCheckoutFormData(
    formData: CashbackFormData,
    redeemCashLoyaltyProgram: LoyaltyProgram | null,
    loyaltyCurrencySelected: LoyaltyCurrency | null,
  ): void {
    // check if formData.transfer amount > store.
    this.pointsSummaryFacade.pointsSummary$
      .pipe(take(1))
      // @ts-ignore
      .subscribe(({ pointsBalance }) => {
        if (formData.sourceAmount > pointsBalance) {
          this.dialogService.open(CheckoutErrorDialogComponent, {
            data: {
              ctaButtonText: this.translateService.instant('modal.error.ok'),
              errorMessageDescription: this.translateService.instant(
                'fulfillment.insufficient_miles',
                {
                  currencyShortName: loyaltyCurrencySelected?.shortName,
                },
              ),
              errorMessageTitle: this.translateService.instant(
                'fulfillment.insufficient_miles_title',
                {
                  loyaltyCurrency: loyaltyCurrencySelected?.shortName,
                },
              ),
              iconName: this.popupSettingService.insufficientMilesIconType,
            },
            id: 'rd-shared-checkout-error-dialog',
            screenMode: 'center-md',
            ariaLabel: this.translateService.instant(
              'fulfillment.insufficient_miles_title',
              {
                loyaltyCurrency: loyaltyCurrencySelected?.shortName,
              },
            ),
          });
        } else {
          this.cashbackFacade.submitCashbackForm({
            loyaltyProgramId: redeemCashLoyaltyProgram!.id,
            formData,
          });

          if (!this.featureFlagService.isAutoRedemptionEnabled) {
            this.router.navigate(['/redeem_cash/review']);
          }
        }
      });
  }

  private loadMembershipsIfLoggedIn(): void {
    this.isLoggedIn$.pipe(take(1)).subscribe((isLoggedIn: boolean | null) => {
      if (isLoggedIn) {
        this.store.dispatch(new LoadMemberships());
      }
    });
  }

  private loadRedeemCashLoyaltyProgramIfItDoesntExist(): void {
    this.redeemCashLoyaltyProgram$
      .pipe(take(1))
      // @ts-ignore
      .subscribe((redeemCashLoyaltyProgram: LoyaltyProgram) => {
        if (!redeemCashLoyaltyProgram) {
          this.store.dispatch(new LoadRedeemCashLoyaltyProgram());
        }
      });
  }

  private validateMembershipEnrollment(
    membership: CashBackMembership,
  ): boolean {
    const { enrollment } = membership;
    if (enrollment && enrollment.status !== 'active') {
      return false;
    }

    return true;
  }
}
